import React, { useRef, useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import axios from 'axios';

import LoginUsuarioExterno from '../../components/UsuarioExterno/LoginUsuario';
import { useLoginStore } from '../../stores/AuthStatusStore';

// Layouts
const LAYOUT_PAGAMENTO = 1;
const LAYOUT_COMISSAO = 2;

export default function ArquivosBRB(): JSX.Element {
  const [loadingState, setLoadingState] = useState<
    'idle' | 'processing' | 'downloading'
  >('idle');
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaValido, setRecaptchaValido] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [lote, setLote] = useState('');

  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const isLogado = useLoginStore(x => x.login);
  const urlBase = process.env.REACT_APP_APIV3_URL;

  useEffect(() => {
    setShowCaptcha(false);
    if (isLogado) {
      const timer = setTimeout(() => {
        setShowCaptcha(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLogado]);

  const inputStyle =
    'border border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-300 rounded-lg p-3 w-full shadow-sm transition-all duration-300';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setLote(value);
    setMessage(null);
  };

  const onCaptchaVerify = (token: string | null) => {
    setRecaptchaValido(!!token);
  };

  const longRequestConfig = {
    timeout: 300000,
    headers: { 'Content-Type': 'application/json' },
  };

  const triggerBrowserDownload = (blob: Blob, fileName: string) => {
    const href = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
  };

  const fetchRelatorioBlob = async (layoutCode: number, numLote: string) => {
    return await axios.post(
      `${urlBase}/baixa-relatorio`,
      {
        layout: layoutCode,
        nu_loteB: Number(numLote),
      },
      {
        responseType: 'blob',
        ...longRequestConfig,
      },
    );
  };

  const handleProcessamento = async (tipo: 'pagamento' | 'comissao') => {
    if (!lote) {
      alert('Por favor, informe o número do lote.');
      return;
    }

    if (!recaptchaValido) {
      alert('Por favor, complete o reCAPTCHA.');
      return;
    }

    const layoutCode =
      tipo === 'pagamento' ? LAYOUT_PAGAMENTO : LAYOUT_COMISSAO;
    const nomeArquivoBase = `BRB_${tipo}_lote_${lote}.txt`;

    try {
      setLoadingState('processing');
      setMessage({
        text: 'Realizando carregamento...',
        type: 'info',
      });

      // PASSO 2: CARGA DOS DADOS
      const endpointCarga =
        tipo === 'pagamento' ? '/carrega-pagamento' : '/carrega-comissao';

      await axios.get(
        `${urlBase}${endpointCarga}?lote=${lote}`,
        longRequestConfig,
      );

      // PASSO 3: DOWNLOAD FINAL
      setLoadingState('downloading');
      setMessage({
        text: 'Carga concluída. Gerando e baixando arquivo final...',
        type: 'info',
      });

      const responseFinal = await fetchRelatorioBlob(layoutCode, lote);

      const fileNameFinal = nomeArquivoBase;

      triggerBrowserDownload(responseFinal.data, fileNameFinal);

      setMessage({
        text: `Arquivo de ${tipo} gerado e baixado com sucesso!`,
        type: 'success',
      });
    } catch (err: any) {
      console.error(err);
      let errorMsg = 'Erro ao processar a solicitação.';

      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json.message) errorMsg = json.message;
        } catch (e) {
          //console.log(e);
        }
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.code === 'ECONNABORTED') {
        errorMsg =
          'O servidor demorou muito para responder. Tente novamente mais tarde.';
      }

      setMessage({ text: errorMsg, type: 'error' });
    } finally {
      setLoadingState('idle');
    }
  };

  const limparCampos = () => {
    setLote('');
    setMessage(null);
    setRecaptchaValido(false);
    if (captchaRef.current) captchaRef.current.reset();
  };

  return !isLogado ? (
    <div className="mx-auto my-auto py-20 rounded">
      <h2 className="text-[2rem] font-semibold text-primary-main mt-12 text-center">
        Login - Arquivos BRB
      </h2>
      <LoginUsuarioExterno />
    </div>
  ) : (
    <div className="mx-auto my-auto py-20 rounded ">
      <div className="max-w-md mx-auto rounded-xl bg-white p-6 shadow-[0_20px_50px_rgba(0,0,0,0.1)] border-gray-50">
        <h2 className="text-2xl font-bold text-gray-700 mb-2 text-center">
          Geração de Arquivos BRB
        </h2>
        <p className="text-center text-gray-500 text-sm mb-6">
          Insira o número do lote. O sistema verificará se o arquivo já existe
          antes de iniciar um novo processamento.
        </p>

        <div className="p-5 gap-4 mt-5 bg-gray-50 rounded-lg border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número do Lote
          </label>
          <input
            type="text"
            name="lote"
            value={lote}
            onChange={handleChange}
            disabled={loadingState !== 'idle'}
            className={`${inputStyle} ${
              loadingState !== 'idle' ? 'bg-gray-100' : ''
            }`}
            placeholder="Ex: 548503"
            autoComplete="off"
          />
        </div>

        <div className="mt-6 flex justify-center" style={{ minHeight: '78px' }}>
          {showCaptcha && (
            <ReCAPTCHA
              key="captcha-brb-unique"
              ref={captchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
              onChange={onCaptchaVerify}
            />
          )}
        </div>

        {message && (
          <div
            className={`mt-4 p-3 rounded text-center text-sm border 
                ${
                  message.type === 'success'
                    ? 'bg-green-100 text-green-700 border-green-200'
                    : message.type === 'error'
                    ? 'bg-red-100 text-red-700 border-red-200'
                    : 'bg-blue-100 text-blue-700 border-blue-200'
                }`}
          >
            {loadingState !== 'idle' && (
              <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></span>
            )}
            {message.text}
          </div>
        )}

        {loadingState === 'idle' && (
          <div className="flex flex-col gap-3 mt-6">
            <button
              onClick={() => handleProcessamento('pagamento')}
              disabled={!lote || !recaptchaValido}
              className={`w-full text-white px-4 py-3 rounded font-medium transition-colors shadow-sm
                        ${
                          !lote || !recaptchaValido
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
            >
              Baixar Pagamento
            </button>

            <button
              onClick={() => handleProcessamento('comissao')}
              disabled={!lote || !recaptchaValido}
              className={`w-full text-white px-4 py-3 rounded font-medium transition-colors shadow-sm
                        ${
                          !lote || !recaptchaValido
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
            >
              Baixar Comissão
            </button>
          </div>
        )}

        {loadingState === 'idle' && (
          <div className="mt-8 border-t pt-4 text-center">
            <button
              onClick={limparCampos}
              className="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Limpar Campos e Reiniciar
            </button>
          </div>
        )}
      </div>
      <div className="mt-4 text-center">
        <button
          onClick={() => {
            window.location.href = '/arquivosbrb';
          }}
          className="px-4 py-2 text-md font-semibold text-red-500 transition-all duration-200 border border-red-500 rounded-lg hover:bg-red-500 hover:text-white"
        >
          Sair
        </button>
      </div>
    </div>
  );
}
